import * as React from "react";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import { StaticImage } from "gatsby-plugin-image";
import {
  card,
  brandLogo,
  imagePriceContainer,
  title4,
  buttonWrapper,
  carouselContainer,
  carouselIcon,
  carouselNextIcon,
  carouselPrevIcon,
} from "./HotelCard.module.scss";
import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";
import fallbackImage from "../../../images/hotel_fallback.jpg";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

export const Card: React.FC<{
  children: React.ReactNode;
  priceClass: string;
  stacked: boolean;
}> = (props) => {
  return (
    <div
      className={`${card} ${props.priceClass}  g-0 align-items-center`}
      {...props}
    >
      <Row className={`g-0 ${props.stacked ? "stacked" : "horizontal"}`}>
        {props.children}
      </Row>
    </div>
  );
};

export const BrandLogo: React.FC<{
  children: React.ReactNode;
  className: string;
}> = (props) => {
  return (
    <div className={`${brandLogo} ${props.className}`}>{props.children}</div>
  );
};

export const ImagePriceContainer: React.FC<{
  children: React.ReactNode;
  class: string;
}> = (props) => {
  return (
    <div className={`${imagePriceContainer} ${props.class}`}>
      {props.children}
    </div>
  );
};

export const Title4: React.FC<{ children: React.ReactNode; id?: string }> = (
  props
) => {
  return (
    <h4
      className={`${title4} title mb-3  d-table-cell align-middle`}
      id={props.id}
      itemProp="name"
    >
      {props.children}
    </h4>
  );
};

export const ButtonWrapper: React.FC<{
  children: React.ReactNode;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  regularText?: boolean;
}> = (props) => {
  return (
    <Button
      className={`${buttonWrapper}  w-100 ${
        props.regularText ? "" : "text-uppercase"
      } mt-lg-0`}
      {...props}
    >
      {props.children}
    </Button>
  );
};
export const CarouselPrevIcon: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <div className={`${carouselIcon} ${carouselPrevIcon}`} onClick={onClick}>
    <StaticImage
      src={"../../../svg/chevron_left.svg"}
      alt="hotel Card Carousel Left Button"
      placeholder="blurred"
      layout="fixed"
      formats={["png", "webp"]}
    />
  </div>
);

export const CarouselNextIcon: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <div className={`${carouselIcon} ${carouselNextIcon}`} onClick={onClick}>
    <StaticImage
      src={"../../../svg/chevron_right.svg"}
      alt="hotel Card Carousel Right Button"
      placeholder="blurred"
      layout="fixed"
      formats={["png", "webp"]}
    />
  </div>
);

interface GalleryImage {
  imageUrl: string;
  altText: string;
  hotelName?: string;
}

interface HotelCarouselProps {
  galleryImages: GalleryImage[];
  isMobile: boolean;
  display?: string;
}

interface GalleryProps {
  galleryImages: GalleryImage[];
  isMobile: boolean;
  display?: string;
}

const Carousel: React.FC<GalleryProps> = ({
  galleryImages,
  isMobile,
  display,
}) => {
  const swiperRef = useRef<SwiperCore | null>(null);
  if (
    galleryImages.length === 1 &&
    galleryImages[0]?.imageUrl === fallbackImage
  ) {
    return (
      <img
        id="hotel-fallback-image"
        className="fallback-image"
        src={galleryImages[0]?.imageUrl}
        alt={galleryImages[0]?.hotelName}
      />
    );
  }
  return (
    <Swiper
      style={{
        "--swiper-pagination-color": "#fff",
        position: "relative",
        "max-width": isMobile
          ? "382px"
          : display === "map"
          ? "337.33px"
          : "353px",
      }}
      lazy={true}
      className="mySwiper"
      loop={true}
      slidesPerView={1}
      onBeforeInit={(swiper) => {
        swiperRef.current = swiper;
      }}
    >
      {galleryImages?.map((image) => (
        <SwiperSlide key={image.imageUrl}>
          <img
            loading="lazy"
            src={image.imageUrl}
            style={{
              width: isMobile
                ? "382px"
                : display === "map"
                ? "337.33px"
                : "353px",
              height: isMobile || display === "map" ? "203px" : "300px",
              borderRadius: isMobile
                ? "6px 6px 0px 0px"
                : display === "map"
                ? "6px 6px 0px 0px"
                : "6px 0px 0px 6px",
            }}
            alt={image.altText}
            onLoad={(event) => {
              const { naturalWidth, naturalHeight } = event.target;
              const aspectRatio = naturalWidth / naturalHeight;
              if (aspectRatio !== 4 / 3) {
                event.target.style.objectFit = "cover";
              }
            }}
          />
          <div className="swiper-lazy-preloader swiper-lazy-preloader-white"></div>
        </SwiperSlide>
      ))}
      <div className={carouselContainer}>
        <CarouselPrevIcon onClick={() => swiperRef.current?.slidePrev()} />
        <CarouselNextIcon onClick={() => swiperRef.current?.slideNext()} />
      </div>
    </Swiper>
  );
};

export const HotelCarousel: React.FC<HotelCarouselProps> = ({
  galleryImages,
  isMobile,
  display,
}) => {
  return (
    <Carousel
      galleryImages={galleryImages}
      isMobile={isMobile}
      display={display}
    />
  );
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength);
  }
  return text;
};

export const decodeHtmlEntities = (text: string): string => {
  const textArea = document.createElement("textarea");
  textArea.innerHTML = text;
  return textArea.value;
};
